import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import useEditorStore from '../../stores/useEditorStore';

/**
 * Blender-style interactive viewport axis gizmo
 * - Shows X, Y, Z axis arrows with colors (red, green, blue)
 * - Click on arrows to snap camera to that axis view
 * - Drag to orbit the view
 * - Center sphere for perspective view
 */
const ViewportGizmo = ({ size = 120 }) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const gizmoGroupRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragModeRef = useRef('orbit'); // 'orbit' or 'pan'
  const dragButtonRef = useRef(0); // 0 = left, 2 = right
  const lastMouseRef = useRef({ x: 0, y: 0 });
  const raycasterRef = useRef(new THREE.Raycaster());
  const mouseRef = useRef(new THREE.Vector2());
  const hoveredAxisRef = useRef(null);
  const isPanningRef = useRef(false);

  // Axis colors (Blender style: X=red, Y=green, Z=blue)
  const AXIS_COLORS = {
    x: 0xff3333,
    y: 0x33ff33,
    z: 0x3333ff,
    hover: 0xffff00,
    sphere: 0x888888
  };

  // Axis directions for camera snapping
  const AXIS_DIRECTIONS = {
    x: { position: [-40, 0, 0], lookAt: [0, 0, 0], label: 'Right' },
    y: { position: [0, 40, 0], lookAt: [0, 0, 0], label: 'Top' },
    z: { position: [0, 0, 40], lookAt: [0, 0, 0], label: 'Front' },
    '-x': { position: [40, 0, 0], lookAt: [0, 0, 0], label: 'Left' },
    '-y': { position: [0, -40, 0], lookAt: [0, 0, 0], label: 'Bottom' },
    '-z': { position: [0, 0, -40], lookAt: [0, 0, 0], label: 'Back' }
  };

  useEffect(() => {
    if (!containerRef.current) return;

    // Setup scene
    const scene = new THREE.Scene();
    scene.background = null; // Transparent
    sceneRef.current = scene;

    // Orthographic camera for consistent gizmo appearance
    const aspect = 1;
    const frustumSize = 2.5;
    const camera = new THREE.OrthographicCamera(
      -frustumSize * aspect,
      frustumSize * aspect,
      frustumSize,
      -frustumSize,
      0.1,
      100
    );
    camera.position.set(2, 2, 2);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true, 
      alpha: true,
      canvas: containerRef.current
    });
    renderer.setSize(size, size);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    rendererRef.current = renderer;

    // Create gizmo group
    const gizmoGroup = new THREE.Group();
    gizmoGroupRef.current = gizmoGroup;
    scene.add(gizmoGroup);

    // Materials
    const materials = {
      x: new THREE.MeshBasicMaterial({ color: AXIS_COLORS.x, transparent: true, opacity: 0.9 }),
      y: new THREE.MeshBasicMaterial({ color: AXIS_COLORS.y, transparent: true, opacity: 0.9 }),
      z: new THREE.MeshBasicMaterial({ color: AXIS_COLORS.z, transparent: true, opacity: 0.9 }),
      sphere: new THREE.MeshBasicMaterial({ color: AXIS_COLORS.sphere, transparent: true, opacity: 0.6 }),
      hover: new THREE.MeshBasicMaterial({ color: AXIS_COLORS.hover, transparent: true, opacity: 1.0 })
    };

    // Store materials for cleanup
    scene.userData.materials = materials;

    // Create center sphere (orbit control)
    const sphereGeo = new THREE.SphereGeometry(0.15, 16, 16);
    const sphere = new THREE.Mesh(sphereGeo, materials.sphere);
    sphere.userData = { type: 'sphere', label: 'Orbit' };
    gizmoGroup.add(sphere);

    // Create axis arrows
    const arrowLength = 1.2;
    const arrowRadius = 0.08;
    const coneHeight = 0.25;
    const coneRadius = 0.15;

    // X Axis (red)
    const xAxis = createAxisArrow(
      materials.x,
      new THREE.Vector3(1, 0, 0),
      arrowLength,
      arrowRadius,
      coneHeight,
      coneRadius,
      'x',
      'X'
    );
    gizmoGroup.add(xAxis);

    // Y Axis (green)
    const yAxis = createAxisArrow(
      materials.y,
      new THREE.Vector3(0, 1, 0),
      arrowLength,
      arrowRadius,
      coneHeight,
      coneRadius,
      'y',
      'Y'
    );
    gizmoGroup.add(yAxis);

    // Z Axis (blue)
    const zAxis = createAxisArrow(
      materials.z,
      new THREE.Vector3(0, 0, 1),
      arrowLength,
      arrowRadius,
      coneHeight,
      coneRadius,
      'z',
      'Z'
    );
    gizmoGroup.add(zAxis);

    // Create dashed negative axis lines
    const dashMat = new THREE.LineDashedMaterial({
      color: 0x888888,
      dashSize: 0.1,
      gapSize: 0.05,
      scale: 1,
      opacity: 0.3,
      transparent: true
    });

    const negLineGeo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(-1.2, 0, 0)
    ]);
    const negXLine = new THREE.Line(negLineGeo, dashMat);
    negXLine.computeLineDistances();
    gizmoGroup.add(negXLine);

    const negYLineGeo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(0, -1.2, 0)
    ]);
    const negYLine = new THREE.Line(negYLineGeo, dashMat.clone());
    negYLine.computeLineDistances();
    gizmoGroup.add(negYLine);

    const negZLineGeo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(0, 0, -1.2)
    ]);
    const negZLine = new THREE.Line(negZLineGeo, dashMat.clone());
    negZLine.computeLineDistances();
    gizmoGroup.add(negZLine);

    // Store negative lines for highlighting
    scene.userData.negLines = { x: negXLine, y: negYLine, z: negZLine };

    // Add subtle lighting for depth perception
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
    scene.add(ambientLight);
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.4);
    dirLight.position.set(1, 1, 1);
    scene.add(dirLight);

    // Animation loop
    let animationId;
    const animate = () => {
      animationId = requestAnimationFrame(animate);
      
      // Sync gizmo rotation with main camera
      const mainCamera = useEditorStore.getState().sceneRefs?.camera;
      if (mainCamera && gizmoGroup) {
        // Make gizmo match camera orientation
        const rotationMatrix = new THREE.Matrix4();
        rotationMatrix.extractRotation(mainCamera.matrixWorld);
        gizmoGroup.quaternion.setFromRotationMatrix(rotationMatrix);
      }

      renderer.render(scene, camera);
    };
    animate();

    // Mouse event handlers
    const getMousePos = (e) => {
      const rect = containerRef.current.getBoundingClientRect();
      return {
        x: ((e.clientX - rect.left) / rect.width) * 2 - 1,
        y: -((e.clientY - rect.top) / rect.height) * 2 + 1
      };
    };

    const onMouseDown = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const pos = getMousePos(e);
      mouseRef.current.set(pos.x, pos.y);
      
      // Raycast to find clicked axis
      raycasterRef.current.setFromCamera(mouseRef.current, camera);
      const intersects = raycasterRef.current.intersectObjects(gizmoGroup.children, true);
      
      if (intersects.length > 0) {
        const hit = findParentAxis(intersects[0].object);
        if (hit) {
          if (hit.userData.type === 'sphere') {
            // Determine drag mode: right-click or shift+left = pan, left = orbit
            const isPanMode = e.button === 2 || (e.button === 0 && e.shiftKey);
            dragModeRef.current = isPanMode ? 'pan' : 'orbit';
            dragButtonRef.current = e.button;
            isDraggingRef.current = true;
            lastMouseRef.current = { x: e.clientX, y: e.clientY };
            isPanningRef.current = isPanMode;
            containerRef.current.style.cursor = isPanMode ? 'move' : 'grabbing';
          } else if (hit.userData.axis) {
            // Snap to axis view
            snapToAxisView(hit.userData.axis, e.shiftKey);
          }
        }
      }
    };

    const onMouseMove = (e) => {
      const pos = getMousePos(e);
      mouseRef.current.set(pos.x, pos.y);
      
      // Raycast for hover effect
      raycasterRef.current.setFromCamera(mouseRef.current, camera);
      const intersects = raycasterRef.current.intersectObjects(gizmoGroup.children, true);
      
      // Reset all materials
      resetMaterials();
      
      if (intersects.length > 0 && !isDraggingRef.current) {
        const hit = findParentAxis(intersects[0].object);
        if (hit) {
          hoveredAxisRef.current = hit;
          if (hit.userData.type === 'sphere') {
            hit.material = materials.hover;
            // Show different cursor hint based on modifiers
            const willPan = e.shiftKey;
            containerRef.current.style.cursor = willPan ? 'move' : 'grab';
          } else if (hit.userData.axis) {
            highlightAxis(hit.userData.axis);
            containerRef.current.style.cursor = 'pointer';
          }
        }
      } else if (!isDraggingRef.current) {
        hoveredAxisRef.current = null;
        containerRef.current.style.cursor = 'default';
      }

      // Handle drag (orbit or pan)
      if (isDraggingRef.current) {
        const deltaX = e.clientX - lastMouseRef.current.x;
        const deltaY = e.clientY - lastMouseRef.current.y;
        
        const controls = useEditorStore.getState().sceneRefs?.controls;
        if (controls) {
          if (dragModeRef.current === 'pan') {
            // Pan the camera target
            const panSpeed = 0.02;
            const target = controls.target.clone();
            
            // Get camera right and up vectors for panning
            const mainCamera = useEditorStore.getState().sceneRefs?.camera;
            if (mainCamera) {
              const right = new THREE.Vector3();
              const up = new THREE.Vector3();
              const direction = new THREE.Vector3();
              
              mainCamera.getWorldDirection(direction);
              right.crossVectors(direction, mainCamera.up).normalize();
              up.copy(mainCamera.up);
              
              // Pan: right-click drag moves in camera plane
              target.addScaledVector(right, -deltaX * panSpeed);
              target.addScaledVector(up, deltaY * panSpeed);
              
              controls.target.copy(target);
              controls.update();
            }
          } else {
            // Orbit the main camera
            const azimuthAngle = controls.getAzimuthalAngle?.() || 0;
            const polarAngle = controls.getPolarAngle?.() || Math.PI / 2;
            
            controls.autoRotate = false;
            controls.setAzimuthalAngle?.(azimuthAngle - deltaX * 0.01);
            controls.setPolarAngle?.(Math.max(0.1, Math.min(Math.PI - 0.1, polarAngle + deltaY * 0.01)));
          }
        }
        
        lastMouseRef.current = { x: e.clientX, y: e.clientY };
      }
    };

    const onMouseUp = () => {
      isDraggingRef.current = false;
      dragModeRef.current = 'orbit';
      isPanningRef.current = false;
      if (containerRef.current) {
        if (hoveredAxisRef.current) {
          containerRef.current.style.cursor = hoveredAxisRef.current.userData.type === 'sphere' ? 'grab' : 'pointer';
        } else {
          containerRef.current.style.cursor = 'default';
        }
      }
    };

    const onMouseLeave = () => {
      resetMaterials();
      hoveredAxisRef.current = null;
    };

    // Helper functions
    function createAxisArrow(material, direction, length, shaftRadius, coneHeight, coneRadius, axis, label) {
      const group = new THREE.Group();
      group.userData = { type: 'axis', axis, label };

      // Shaft (cylinder)
      const shaftGeo = new THREE.CylinderGeometry(shaftRadius, shaftRadius, length, 12);
      const shaft = new THREE.Mesh(shaftGeo, material.clone());
      shaft.userData = { type: 'axis', axis, label };
      
      // Position and orient shaft
      const midPoint = direction.clone().multiplyScalar(length / 2);
      shaft.position.copy(midPoint);
      shaft.lookAt(direction.clone().multiplyScalar(length));
      shaft.rotateX(Math.PI / 2);
      group.add(shaft);

      // Arrow cone
      const coneGeo = new THREE.ConeGeometry(coneRadius, coneHeight, 12);
      const cone = new THREE.Mesh(coneGeo, material.clone());
      cone.userData = { type: 'axis', axis, label };
      cone.position.copy(direction.clone().multiplyScalar(length));
      cone.lookAt(direction.clone().multiplyScalar(length + 1));
      cone.rotateX(Math.PI / 2);
      group.add(cone);

      // Label text (simplified as small sphere for now)
      const labelGeo = new THREE.SphereGeometry(0.08, 8, 8);
      const labelMesh = new THREE.Mesh(labelGeo, material.clone());
      labelMesh.userData = { type: 'axis', axis, label };
      labelMesh.position.copy(direction.clone().multiplyScalar(length + 0.35));
      group.add(labelMesh);

      return group;
    }

    function findParentAxis(mesh) {
      let current = mesh;
      while (current) {
        if (current.userData.type === 'axis' || current.userData.type === 'sphere') {
          return current;
        }
        current = current.parent;
      }
      return null;
    }

    function highlightAxis(axis) {
      gizmoGroup.children.forEach(child => {
        if (child.userData.axis === axis) {
          child.traverse(c => {
            if (c.isMesh && c.userData.type === 'axis') {
              c.material = materials.hover;
            }
          });
        }
      });
      // Also highlight negative line
      const negLine = scene.userData.negLines?.[axis];
      if (negLine) {
        negLine.material = new THREE.LineDashedMaterial({
          color: AXIS_COLORS.hover,
          dashSize: 0.1,
          gapSize: 0.05,
          scale: 1,
          opacity: 0.6,
          transparent: true
        });
        negLine.computeLineDistances();
      }
    }

    function resetMaterials() {
      gizmoGroup.children.forEach(child => {
        if (child.userData.type === 'axis') {
          const originalMat = materials[child.userData.axis];
          child.traverse(c => {
            if (c.isMesh && c.userData.type === 'axis') {
              c.material = originalMat.clone();
            }
          });
        } else if (child.userData.type === 'sphere') {
          child.material = materials.sphere;
        }
      });
      // Reset negative lines
      const dashMat = new THREE.LineDashedMaterial({
        color: 0x888888,
        dashSize: 0.1,
        gapSize: 0.05,
        scale: 1,
        opacity: 0.3,
        transparent: true
      });
      Object.values(scene.userData.negLines || {}).forEach(line => {
        line.material = dashMat;
        line.computeLineDistances();
      });
    }

    function snapToAxisView(axis, isNegative) {
      const setCameraPosition = useEditorStore.getState().setCameraPosition;
      const dirKey = isNegative ? `-${axis}` : axis;
      const direction = AXIS_DIRECTIONS[dirKey] || AXIS_DIRECTIONS[axis];
      
      setCameraPosition({
        x: direction.position[0],
        y: direction.position[1],
        z: direction.position[2]
      });
    }

    // Prevent context menu on right-click for panning
    const onContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Attach events
    const canvas = containerRef.current;
    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('mouseleave', onMouseLeave);
    canvas.addEventListener('contextmenu', onContextMenu);

    // Cleanup
    return () => {
      cancelAnimationFrame(animationId);
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      canvas.removeEventListener('mouseleave', onMouseLeave);
      canvas.removeEventListener('contextmenu', onContextMenu);
      
      // Dispose materials
      if (scene.userData.materials) {
        Object.values(scene.userData.materials).forEach(m => m.dispose());
      }
      
      // Dispose geometries
      gizmoGroup.traverse(child => {
        if (child.geometry) child.geometry.dispose();
      });
      
      renderer.dispose();
    };
  }, [size]);

  return (
    <canvas
      ref={containerRef}
      width={size}
      height={size}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        cursor: 'default',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        touchAction: 'none'
      }}
    />
  );
};

export default ViewportGizmo;
